import { LegalHeader } from "./LegalHeader";
import { Box, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { LegalLink } from "./LegalLink";
import { ContentDivider } from "./ContentDivider";
import { SectionTitle } from "../../../components/text/SectionTitle";
import { useMobileBreakpoint } from "../../../utils/useBreakpoints";

export function AiPolicy() {
  const isMobile = useMobileBreakpoint();
  const marginBetweenSections = isMobile ? "24px" : "40px";

  return (
    <>
      <LegalHeader
        title="Artificial Intelligence Content Policy"
        lastUpdated="March 20, 2026"
      />
      <SectionTitle mb={marginBetweenSections}>TLDR;</SectionTitle>
      <UnorderedList marginLeft="24px">
        <ListItem>
          <Text variant="legalContent">
            GlobalComix changes policy to remove fully AI generated works
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            These works have been hidden today from the platform
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Our content team is working through finding mislabeled/undisclosed
            AI contents
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            We support using AI tools supporting creator workflows, but not as
            fully generated
          </Text>
        </ListItem>
      </UnorderedList>
      <Text variant="legalContent">
        GlobalComix is, first and foremost, a home for human creativity.
      </Text>
      <Text variant="legalContent">
        We recognize that emerging technologies, including Artificial
        Intelligence (AI), can be used as creative tools, but also that their
        use raises valid concerns about originality, respect for artists' rights
        and quality.
      </Text>
      <Text variant="legalContent">In this policy, we wanted to:</Text>
      <UnorderedList marginLeft="24px">
        <ListItem>
          <Text variant="legalContent">
            Prioritize and protect human artistry
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Protect our creators and their work
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Enable creativity and expression across skills and use cases
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Create forward-looking guidelines and create a stable environment
            for creators as tools and technologies continue to evolve
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Set clear expectations for when and how AI-generated or AI-assisted
            materials can appear and be monetized on GlobalComix.
          </Text>
        </ListItem>
      </UnorderedList>
      <ContentDivider />
      <SectionTitle mb={marginBetweenSections}>Our Principles</SectionTitle>
      <Text variant="legalContent">
        GlobalComix celebrates human storytelling, art, and originality.
      </Text>
      <Text variant="legalContent">
        We want creators to be able to thrive from their art and artistry.
      </Text>
      <Text variant="legalContent">
        AI tools may be used to assist creative work, but they should never
        fully remove the human from the workflow.
      </Text>
      <ContentDivider />
      <SectionTitle mb={marginBetweenSections}>
        When AI Use Is Acceptable in Comics
      </SectionTitle>
      <UnorderedList marginLeft="24px">
        <ListItem>
          <Text variant="legalContent">
            The uploader has full rights and licenses to use the AI tools and
            any resulting outputs.
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            AI is used for supporting or enhancing a creator's original work
            (for example, coloring assistance, idea exploration, background
            generation, effects, animation).
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            When AI tools are used as a part in the broader creative workflow in
            concert with human artistry.
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            The creator discloses AI involvement clearly when it forms a
            meaningful part of the artwork or writing (e.g., in the release
            description or credits).
          </Text>
        </ListItem>
      </UnorderedList>
      <Text variant="legalContent">
        GlobalComix may provide exceptions to our policy on the use of AI with
        regards to content creation when considering the guidelines above.
      </Text>
      <Text variant="legalContent">
        If GlobalComix requests material documenting licenses to characters,
        likenesses, intellectual property, or copyrights, it is the burden of
        the uploader to provide said material. When evidence is not provided in
        a timely manner, we will assume that these criteria have not been met.
      </Text>
      <Text variant="legalContent">
        Anyone considering uploading content utilizing generative AI should
        email{" "}
        <LegalLink href="mailto:content-violation@globalcomix.com">
          content-violation@globalcomix.com
        </LegalLink>{" "}
        and a member of our team will review the submission.
      </Text>
      <ContentDivider />
      <SectionTitle mb={marginBetweenSections}>
        When AI Use Is Not Acceptable
      </SectionTitle>
      <Text variant="legalContent">
        Content that violates any of the following will not be allowed:
      </Text>
      <UnorderedList marginLeft="24px">
        <ListItem>
          <Text variant="legalContent">
            Generated AI graphics that imitate or copy another creator's
            intellectual property, style, likeness, or characters without
            permission.
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Works that misrepresent AI outputs as entirely human-made.
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            AI-generated content that violates GlobalComix's broader obscenity
            or hate-speech rules, including depictions of sexualized minors or
            sexual violence.
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Works where visuals are made fully via Gen AI prompts, instead of
            using AI as just one tool among many in a broader creative workflow.
          </Text>
        </ListItem>
      </UnorderedList>
      <Text variant="legalContent">
        If there is doubt about the origin of a work, GlobalComix may ask for
        supporting information (such as which AI tools were used, or
        documentation of rights).
      </Text>
      <Text variant="legalContent">
        Anyone considering uploading content utilizing generative AI should
        email{" "}
        <LegalLink href="mailto:content-violation@globalcomix.com">
          content-violation@globalcomix.com
        </LegalLink>{" "}
        and a member of our team will review the submission.
      </Text>
      <ContentDivider />
      <SectionTitle mb={marginBetweenSections}>
        Protecting Creator Rights
      </SectionTitle>
      <UnorderedList marginLeft="24px">
        <ListItem>
          <Text variant="legalContent">
            GlobalComix will not use creator-uploaded content to train
            Generative Visual AI models without the creator's explicit opt-in
            consent if such systems are ever developed.
          </Text>
        </ListItem>
        <ListItem>
          <Text variant="legalContent">
            Uploading content to GlobalComix does not grant third parties
            permission to train AI on that work unless the creator explicitly
            grants permission.
          </Text>
        </ListItem>
      </UnorderedList>
      <ContentDivider />
      <SectionTitle mb={marginBetweenSections}>
        Monetization of AI-Generated Work
      </SectionTitle>
      <Text variant="legalContent">
        Content that is created with human artistry, but uses AI assisted tools
        (examples listed in section 7) can be monetized.
      </Text>
      <Text variant="legalContent">
        GlobalComix reserves the right to decide what qualifies as acceptable
        content and may remove monetization from any work it determines lacks
        sufficient original artistry from the creator.
      </Text>
      <ContentDivider />
      <SectionTitle mb={marginBetweenSections}>
        Review and Updates
      </SectionTitle>
      <Text variant="legalContent">
        As laws, technologies, and community standards evolve, this policy may
        be updated.
      </Text>
      <Text variant="legalContent">
        GlobalComix will publish updates explaining how new policies affect both
        human and AI-assisted creators.
      </Text>
      <Text variant="legalContent">
        Questions or concerns about AI use can be sent to{" "}
        <LegalLink href="mailto:content-violation@globalcomix.com">
          content-violation@globalcomix.com
        </LegalLink>
        .
      </Text>
      <ContentDivider />
      <SectionTitle mb={marginBetweenSections}>
        Examples of acceptable use of AI
      </SectionTitle>
      <Text variant="legalContent" fontWeight="semibold" mt="16px">
        Clip Studio Colorize & Smooth, Tone Remove features
      </Text>
      <Text variant="legalContent">
        <LegalLink href="https://help.clip-studio.com/en-us/manual_en/390_filters/AI_Tools.htm#1364086">
          https://help.clip-studio.com/en-us/manual_en/390_filters/AI_Tools.htm
        </LegalLink>
      </Text>
      <Text variant="legalContent">
        Reasoning: These features empower creators and work as a small part of
        the creative process. Subjectivity, taste and creative decision-making
        is used extensively while creating the end product.
      </Text>
      <Text variant="legalContent" fontWeight="semibold" mt="16px">
        Adobe Illustrator AI features
      </Text>
      <Text variant="legalContent">
        <LegalLink href="https://www.adobe.com/learn/illustrator/web/illustrator-generative-ai-introduction">
          https://www.adobe.com/learn/illustrator/web/illustrator-generative-ai-introduction
        </LegalLink>
      </Text>
      <Text variant="legalContent">
        Reasoning: These features empower creators and work as a small part of
        the creative process. Subjectivity, taste and creative decision-making
        is used extensively while creating the end product.
      </Text>
      <Text variant="legalContent" fontWeight="semibold" mt="16px">
        Adobe Photoshop AI features (including generative fill)
      </Text>
      <Text variant="legalContent">
        <LegalLink href="https://www.adobe.com/products/photoshop/ai.html">
          https://www.adobe.com/products/photoshop/ai.html
        </LegalLink>
      </Text>
      <Text variant="legalContent">
        Reasoning: These features empower creators and work as a small part of
        the creative process. Subjectivity, taste and creative decision-making
        is used extensively while creating the end product.
      </Text>
      <Text variant="legalContent" fontWeight="semibold" mt="16px">
        Custom Trained Visual Generation Models
      </Text>
      <Text variant="legalContent">
        Reasoning: It's acceptable to use Generative AI to create visuals based
        on original and owned artwork. Original character design and world
        building is required to train the model, and the output needs to be
        composed to a final comic product with artistry and creative
        decision-making.
      </Text>
      <Text variant="legalContent" fontWeight="semibold" mt="16px">
        Retouching AI features
      </Text>
      <Text variant="legalContent">
        Reasoning: Using software and tools that edit original artwork (e.g.
        changing colors, up-scaling, smoothing, changing select details) is
        allowed as they work as a single step in a larger workflow that requires
        subjectivity and creative decision-making.
      </Text>
      <Box pb="120px" />
    </>
  );
}
